import { useEffect, useState } from "react";
import type { TargetRect } from "./types";

const IS_DEV =
  typeof process !== "undefined" && process.env?.NODE_ENV !== "production";

export function useTargetRect(selector: string | null): TargetRect | null {
  const [rect, setRect] = useState<TargetRect | null>(null);

  useEffect(() => {
    if (!selector) {
      setRect(null);
      return;
    }

    const element = document.querySelector(selector);
    if (!(element instanceof HTMLElement)) {
      if (IS_DEV) {
        console.warn(
          "[onboarding-tour] target not found in DOM:",
          selector,
          "(check that the element is rendered + the data-onboarding attribute matches)"
        );
      }
      setRect(null);
      return;
    }
    if (IS_DEV) {
      console.info("[onboarding-tour] target resolved:", selector);
    }

    element.scrollIntoView({ block: "center", behavior: "smooth" });

    const read = () => {
      const r = element.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };

    read();
    const resizeObserver = new ResizeObserver(read);
    resizeObserver.observe(element);
    window.addEventListener("scroll", read, true);
    window.addEventListener("resize", read);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("scroll", read, true);
      window.removeEventListener("resize", read);
    };
  }, [selector]);

  return rect;
}
